import {useContext, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import FormComment, {FORM_COMMENT_SPACING} from '@adv-mt/mt-form-comment'
import ListComments from '@adv-mt/mt-list-comments'
import LoginBanner from '@adv-mt/mt-login-banner'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {DEFAULT_COMMENTS_COUNT, DEFAULT_COMMENTS_OFFSET, NEWS_DETAIL_EXTRA_INFO_BASE_CLASS} from '../config.js'

const COMMENTS_BASE_CLASS = `${NEWS_DETAIL_EXTRA_INFO_BASE_CLASS}Comments`
const POST_CLASS = `${COMMENTS_BASE_CLASS}Post`
const SHOW_MORE_CLASS = `${COMMENTS_BASE_CLASS}ShowMore`

export const CommentsAndReviewsSection = ({allowComments, articleId, comments = [], hideSectionTitle, title}) => {
  const {domain, i18n} = useContext(Context)
  const {user: currentUser, isLogged} = useContext(AuthenticationContext)
  const {location} = useRouter()
  const {track} = useAnalyticsTrack()

  const config = domain.get('config')

  const {LOGIN_SOURCE_ID_CREATE_NEWS_COMMENT} = config.get('login')
  const URL_STATICS_IMAGES = config.get('URL_STATICS_IMAGES')
  const {HIT_ORIGINS} = config.get('tracking')

  const [commentsList, setCommentsList] = useState([...comments])
  const [offset, setOffset] = useState(DEFAULT_COMMENTS_OFFSET)
  const [isLoading, setIsLoading] = useState(false)
  const [renderMoreCommentsAction, setRenderMoreCommentsAction] = useState(shouldRenderShowMoreAction(comments))

  const hasComments = Boolean(commentsList.length)
  const domainConfig = domain.get('config')
  const {ARTICLE_COMMENTS_ANCHOR} = domainConfig.get('editorial')
  const isWebview = domainConfig.get('isWebview')

  const sectionRef = useRef()
  const scrollToSection = () => {
    setTimeout(() => {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      })
    }, 600)
  }

  useMount(() => {
    if (location.hash.includes(ARTICLE_COMMENTS_ANCHOR)) {
      scrollToSection()
    }
  })

  // TODO: will be updated when we add reviews/stars/ratings
  if (!allowComments || isWebview) return null

  const {sectionTitle, loginBannerTitle, loginBannerDescription, leaveCommentInputPlaceholder, showMoreComments} =
    getLiterals(i18n, {
      hideSectionTitle,
      title
    })

  const getComments = async pageNumber => {
    const getCommentsParams = {
      articleId,
      items: DEFAULT_COMMENTS_COUNT,
      pageNumber
    }

    const [commentsError, {comments}] = await domain
      .get('get_news_comments_reviews_use_case')
      .execute(getCommentsParams)
    if (commentsError) return []

    return comments
  }

  const handleClickShowMore = async () => {
    setIsLoading(true)

    const updatedOffsetValue = offset + 1
    const newComments = await getComments(updatedOffsetValue)

    setRenderMoreCommentsAction(shouldRenderShowMoreAction(newComments))
    setCommentsList([...commentsList, ...newComments])
    setOffset(updatedOffsetValue)
    setIsLoading(false)
  }

  const onPublish = newCommentData => {
    const updatedCommentsList = getUpdatedCommentsList({
      commentsList,
      newCommentData,
      currentUser
    })

    setCommentsList(updatedCommentsList)
  }

  const onAfterReplyComment = async newCommentData => {
    onPublish(newCommentData)

    const {TRACKING_EVENT_NAMES} = domainConfig.get('tracking')
    const replyEvent = await domain.get('get_event_tracking_data_use_case').execute({
      article: {
        title
      },
      event: TRACKING_EVENT_NAMES.NEWS_COMMENT_REPLY
    })

    track(replyEvent)
  }

  const onAfterPostComment = async newCommentData => {
    onPublish(newCommentData)

    const {TRACKING_EVENT_NAMES} = domainConfig.get('tracking')
    const publishEvent = await domain.get('get_event_tracking_data_use_case').execute({
      article: {
        title
      },
      event: TRACKING_EVENT_NAMES.NEWS_COMMENT_PUBLISH
    })

    track(publishEvent)
  }

  return (
    <div ref={sectionRef} id={ARTICLE_COMMENTS_ANCHOR} className={COMMENTS_BASE_CLASS}>
      <div className={`${NEWS_DETAIL_EXTRA_INFO_BASE_CLASS}TitleWrapper`}>
        <TitleBasic title={sectionTitle} as={titleBasicTags.h2} size={titleBasicSizes.s} />
      </div>

      <div className={POST_CLASS}>
        {isLogged && (
          <FormComment
            {...adaptToMainFormCommentProps({
              articleId,
              currentUser,
              inputPlaceholder: leaveCommentInputPlaceholder,
              onAfterPublish: onAfterPostComment
            })}
          />
        )}
        {!isLogged && (
          <LoginBanner
            title={loginBannerTitle}
            description={loginBannerDescription}
            imageSrc={`${URL_STATICS_IMAGES}illustrations/comments.svg`}
            modalLoginSource={LOGIN_SOURCE_ID_CREATE_NEWS_COMMENT}
            trackingOrigin={HIT_ORIGINS.NEWS_COMMENT}
            loginLess
          />
        )}
      </div>
      {hasComments && (
        <>
          <ListComments
            comments={commentsList}
            publishCommentPartialProps={adaptToReplyFormCommentPartialProps({
              articleId,
              currentUser,
              onAfterPublish: onAfterReplyComment
            })}
          />
          {renderMoreCommentsAction && (
            <div className={SHOW_MORE_CLASS}>
              <AtomButton
                size={atomButtonSizes.SMALL}
                shape={atomButtonShapes.CIRCULAR}
                design={atomButtonDesigns.OUTLINE}
                onClick={handleClickShowMore}
                isLoading={isLoading}
              >
                {showMoreComments}
              </AtomButton>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function shouldRenderShowMoreAction(comments) {
  return Boolean(comments.length >= DEFAULT_COMMENTS_COUNT)
}

function getLiterals(i18n, {hideSectionTitle, title}) {
  return {
    sectionTitle: hideSectionTitle ? '' : i18n.t('COMMENTS.SECTION_TITLE', {title}),
    loginBannerTitle: i18n.t('LOGIN_BANNER.NEWS_COMMENTS.TITLE'),
    loginBannerDescription: i18n.t('LOGIN_BANNER.NEWS_COMMENTS.DESCRIPTION'),
    leaveCommentInputPlaceholder: i18n.t('COMMENTS.INPUT_PLACEHOLDER_COMMENT'),
    showMoreComments: i18n.t('COMMENTS.SHOW_MORE')
  }
}

function adaptUserProp({currentUser}) {
  const {userId, name, avatar} = currentUser

  return {
    id: userId,
    name,
    photoUrl: avatar
  }
}

function adaptToMainFormCommentProps({articleId, currentUser, inputPlaceholder, onAfterPublish}) {
  return {
    publish: {
      button: {
        color: atomButtonColors.PRIMARY,
        design: atomButtonDesigns.SOLID,
        showIcon: true
      },
      data: {
        articleId
      },
      onAfterPublish
    },
    user: adaptUserProp({currentUser}),
    inputPlaceholder,
    shadow: true,
    spacing: FORM_COMMENT_SPACING.MEDIUM,
    showTitle: true
  }
}

function adaptToReplyFormCommentPartialProps({articleId, currentUser, onAfterPublish}) {
  return {
    articleId,
    onPublishSuccess: onAfterPublish,
    user: currentUser ? adaptUserProp({currentUser}) : null
  }
}

function getUpdatedCommentsList({commentsList, newCommentData, currentUser}) {
  const {parentCommentId} = newCommentData

  const updateCommentRepliesList = () => {
    return commentsList.map(comment => {
      const {id, answers} = comment
      const shouldExtendAnswers = parentCommentId === id

      if (shouldExtendAnswers) {
        return {
          ...comment,
          answers: [
            ...answers,
            {
              ...createComment({
                ...newCommentData,
                currentUser
              }),
              isReply: true
            }
          ]
        }
      }

      return {...comment}
    })
  }

  const updateMainComments = () => {
    return [
      createComment({
        ...newCommentData,
        currentUser
      }),
      ...commentsList
    ]
  }

  return parentCommentId ? updateCommentRepliesList() : updateMainComments()
}

// TODO parentCommentId will be used to generate a answers collection
// https://jira.ets.mpi-internal.com/browse/MTR-67340
function createComment({newCommentId, newCommentText, currentUser}) {
  const {name, avatar} = currentUser

  return {
    id: newCommentId,
    author: {
      name,
      photoUrl: avatar
    },
    date: Date.now(),
    text: newCommentText,
    isNew: true,
    answers: []
  }
}

CommentsAndReviewsSection.propTypes = {
  allowComments: PropTypes.bool,
  articleId: PropTypes.string,
  comments: PropTypes.array,
  hideSectionTitle: PropTypes.bool,
  title: PropTypes.string
}
